// One map-level mousemove handler per Mapbox instance sets the pointer cursor
// for every registered interactive layer with a single queryRenderedFeatures
// call. Mapbox's per-layer delegated listeners (map.on('mousemove', layerId))
// each run their own query on every mouse move, which scales with layer count.
const registries = new WeakMap()

const THROTTLE_MS = 50

function getRegistry(map) {
  let reg = registries.get(map)
  if (reg) return reg
  reg = { layers: new Set(), hovering: false, last: 0, pending: null }

  const setCursor = (cursor) => {
    const canvas = map.getCanvas?.()
    if (canvas) canvas.style.cursor = cursor
  }

  const check = (point) => {
    if (!map.style) return
    const ids = [...reg.layers].filter((id) => map.getLayer(id))
    let hit = false
    if (ids.length) {
      try {
        hit = map.queryRenderedFeatures(point, { layers: ids }).length > 0
      } catch (_) {
        hit = false
      }
    }
    if (hit !== reg.hovering) {
      reg.hovering = hit
      setCursor(hit ? 'pointer' : '')
    }
  }

  reg.onMove = (e) => {
    const now = Date.now()
    if (now - reg.last >= THROTTLE_MS) {
      reg.last = now
      check(e.point)
    } else if (!reg.pending) {
      reg.pending = setTimeout(() => {
        reg.pending = null
        reg.last = Date.now()
        check(e.point)
      }, THROTTLE_MS)
    }
  }

  reg.onOut = () => {
    if (reg.pending) {
      clearTimeout(reg.pending)
      reg.pending = null
    }
    if (reg.hovering) {
      reg.hovering = false
      setCursor('')
    }
  }

  map.on('mousemove', reg.onMove)
  map.on('mouseout', reg.onOut)
  registries.set(map, reg)
  return reg
}

export function registerHoverLayer(map, layerId) {
  if (!map || !layerId) return
  getRegistry(map).layers.add(layerId)
}

export function unregisterHoverLayer(map, layerId) {
  const reg = registries.get(map)
  if (!reg) return
  reg.layers.delete(layerId)
  if (reg.layers.size > 0) return
  if (reg.pending) clearTimeout(reg.pending)
  try {
    map.off('mousemove', reg.onMove)
    map.off('mouseout', reg.onOut)
  } catch (_) { /* instance being disposed */ }
  if (reg.hovering) {
    const canvas = map.getCanvas?.()
    if (canvas) canvas.style.cursor = ''
  }
  registries.delete(map)
}
